import {
  afterRenderEffect,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyChatModule } from '@vendasta/galaxy/chat';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConversationChannel, EventType, ParticipantType } from '@vendasta/conversation';
import { EmailPreviewDialogComponent, EmailPreviewDialogData } from '../preview/email-preview.component';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, first, firstValueFrom, map, of } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ContentContentType, MessageService } from '@vendasta/email';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { resolveCallRecordingUrl } from '../../../../../../../core/src/lib/resolve-call-recording-url';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  AiAssistant,
  AiAssistantService,
  aiAssistantAssetUrl,
  ASSISTANT_ID_VOICE_RECEPTIONIST,
  VENDASTA_AI_AVATAR_SVG_ICON,
} from '@galaxy/ai-assistant';

import { CallTranscriptModalComponent } from './call-transcript-modal.component';
import { InboxEvaluationButtonsComponent } from '../inbox-evaluation-buttons/inbox-evaluation-buttons.component';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN } from '../../../../../../../core/src/lib/tokens';
import { ConversationService } from '../../../../../../../core/src/lib/state/conversation.service';
import {
  isPersonalEmailThreadEvent,
  WITHHELD_PERSONAL_EMAIL_PREVIEW,
  NO_PERSONAL_EMAIL_PREVIEW,
} from '../../../../../../../core/src/lib/channels/personal-email-preview';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ConversationEvent, EventInfo } from '../../../../../../../core/src/lib/interface/conversation.interface';
import { htmlToPlainText } from '../../../../../../../core/src/lib/conversation-utils';
import { ConversationPipesModule } from '../../../../../../../pipes';

import {
  metadataCallSourceDrill1Key,
  aiVoiceReceptionist,
  eventMetadataDeadConversationIDKey,
} from '../inbox-item-hydration.service';

const metadataMissedCallKey = 'event_missed_call';

@Component({
  selector: 'inbox-event',
  imports: [
    CommonModule,
    GalaxyAvatarModule,
    TranslateModule,
    GalaxyChatModule,
    ConversationPipesModule,
    InboxEvaluationButtonsComponent,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './inbox-event.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './inbox-event.component.scss',
})
export class InboxEventComponent {
  @HostBinding('class') class = 'inbox-event';

  event = input.required<ConversationEvent>();
  readonly messageTime = input<string | null | undefined>();
  readonly alwaysShowMetadata = input(false, { transform: booleanAttribute });

  private readonly dialog = inject(MatDialog);
  private readonly emailApiService = inject(MessageService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translateService = inject(TranslateService);
  private readonly conversationService = inject(ConversationService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  protected showEvaluationButtonsComponent = computed(() => {
    const event = this.event();
    const metadata = event?.event?.metadata || [];
    return (
      event.event.type === EventType.EVENT_TYPE_PHONE_CALL &&
      !metadata.find((meta) => meta.key === metadataMissedCallKey && meta.value === 'true')
    );
  });
  protected readonly isPersonalEmailThread = computed(() => isPersonalEmailThreadEvent(this.event()?.event));

  private readonly emailPreview = toSignal(
    toObservable(this.event).pipe(
      map((wrapper) =>
        isPersonalEmailThreadEvent(wrapper?.event)
          ? (wrapper.event.metadata?.find((meta) => meta.key === eventMetadataDeadConversationIDKey)?.value ?? '')
          : '',
      ),
      distinctUntilChanged(),
      switchMap((threadedConversationId) =>
        threadedConversationId
          ? this.conversationService.getPersonalEmailPreview(threadedConversationId).pipe(
              startWith(NO_PERSONAL_EMAIL_PREVIEW),
              catchError(() => of(WITHHELD_PERSONAL_EMAIL_PREVIEW)),
            )
          : of(NO_PERSONAL_EMAIL_PREVIEW),
      ),
    ),
    { initialValue: NO_PERSONAL_EMAIL_PREVIEW },
  );

  // Text only on a resolved preview, so nothing reads past a gate that did not run. Empty subject
  // keeps the channel sentence; withheld says the content is private.
  protected readonly emailPreviewKind = computed(() => this.emailPreview().kind);
  protected readonly emailSubject = computed(() => {
    const preview = this.emailPreview();
    return this.isPersonalEmailThread() && preview.kind === 'resolved' ? preview.subject : '';
  });
  /**
   * Markup, bound straight to innerHTML for Angular's sanitizer to handle.
   *
   * Deliberately not through `formatMessage`: that pipe turns newlines into markdown hard breaks
   * and parses the result, which is right for a composer or an assistant reply and wrong for a mail
   * body. Markdown re-enters after any blank line, so an indented tag becomes a literal code block,
   * a line opening `1998.` becomes an ordered list, and one opening `# ` becomes a heading.
   *
   * Empty of text means empty: a body that is only a spacer paragraph or a stripped tracking pixel
   * is still a non-empty string, and taking this branch on one would render a blank card and hide
   * the withheld and event-content branches beneath it.
   */
  protected readonly emailSnippet = computed(() => {
    const preview = this.emailPreview();
    const markup = preview.kind === 'resolved' ? preview.snippet : '';
    return htmlToPlainText(markup) ? markup : '';
  });

  /**
   * Whether the body outgrew the height the card allows it, which is the only case the fade over the
   * cut belongs on: drawn unconditionally it greys out the last line of a message that already ended.
   * CSS cannot compare the two heights, so it is measured.
   */
  protected readonly emailPreviewClipped = signal(false);

  private readonly emailPreviewBody = viewChild<ElementRef<HTMLElement>>('emailPreviewBody');

  protected readonly EventType = EventType;
  protected readonly ConversationChannel = ConversationChannel;
  protected readonly avatarSize = 36;

  showEvaluationButtons = false;

  readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);

  private readonly document = inject(DOCUMENT);
  private readonly aiAssistantService = inject(AiAssistantService, { optional: true });

  private readonly callAssistant = toSignal(
    combineLatest([
      toObservable(this.event).pipe(
        map(
          (event) =>
            event.eventInfo?.assistantId ||
            (event.eventInfo?.isAIVoiceCall ? ASSISTANT_ID_VOICE_RECEPTIONIST : ''),
        ),
        distinctUntilChanged(),
      ),
      this.partnerId$,
      this.accountGroupId$,
    ]).pipe(
      switchMap(([assistantId, partnerId, accountGroupId]) => {
        if (!assistantId || !this.aiAssistantService) {
          return of<AiAssistant | null>(null);
        }
        return this.aiAssistantService
          .getHydratedAssistant(partnerId, accountGroupId, assistantId)
          .pipe(catchError(() => of<AiAssistant | null>(null)));
      }),
    ),
    { initialValue: null },
  );

  readonly assistantAvatarUrl = computed(() => {
    const assistant = this.callAssistant();
    const iconName = assistant?.decoration?.defaultAvatarIcon || VENDASTA_AI_AVATAR_SVG_ICON;
    return (
      assistant?.assistant?.avatarUrl ||
      assistant?.decoration?.avatarIconUrl ||
      aiAssistantAssetUrl(iconName, this.document) ||
      ''
    );
  });

  readonly assistantDisplayName = computed(
    () => this.callAssistant()?.assistant?.name || this.event().eventInfo?.assistantName || '',
  );

  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly isExtraSmall = this.breakpointObserver.observe(Breakpoints.XSmall);

  constructor(private readonly http: HttpClient) {
    this.observeEmailPreviewOverflow();
  }

  async showOriginalEmail(event: EventInfo): Promise<void> {
    if (!event.emailId) return;

    firstValueFrom(
      this.emailApiService
        .getMessage(event.emailId)
        .pipe(map((result) => result.content.filter((content) => content.type === ContentContentType.TEXT_HTML)[0])),
    )
      .then((email) => this.openEmailPreviewDialog(email.value))
      .catch(() =>
        this.snackbarService.openErrorSnack(
          this.translateService.instant('INBOX.ERROR.ORIGINAL_MSG_CONTENT_NOT_AVAILABLE'),
        ),
      );
  }

  openEmailPreviewDialog(emailHtml?: string): void {
    if (!emailHtml) return;

    this.dialog.open(EmailPreviewDialogComponent, {
      data: {
        htmlContent: emailHtml,
      } as EmailPreviewDialogData,
      panelClass: 'mat-dialog-no-padding',
    });
  }

  async openTranscriptModal() {
    const customerName$ = this.conversationService.currentConversationDetail$.pipe(
      first(),
      map(
        (conversationDetail) =>
          conversationDetail?.participants.find(
            (participant) => participant.participantType === ParticipantType.PARTICIPANT_TYPE_CUSTOMER,
          )?.name,
      ),
    );

    const [partnerId, accountGroupId, customerName] = await firstValueFrom(
      combineLatest([this.partnerId$, this.accountGroupId$, customerName$]),
    );
    const eventInfo = this.event().eventInfo;
    if (!eventInfo) return;

    let toName = '';
    let fromName = '';
    if (eventInfo.isAIVoiceCall) {
      const metaData = new Map(this.event().event.metadata?.map((m) => [m.key, m.value]) || []);
      toName = metaData.get(metadataCallSourceDrill1Key) || aiVoiceReceptionist;
      if (customerName) fromName = customerName;
    }

    const dialogRef = this.dialog.open(CallTranscriptModalComponent, {
      width: '950px',
      maxWidth: '100vw',
      maxHeight: '100dvh',
      // dialog's citations component resolves ConversationService from this injector
      injector: this.injector,
      data: {
        partnerId,
        accountGroupId,
        callRecordId: eventInfo.callRecordId,
        transcriptJSON: eventInfo.transcriptJSON,
        toName: toName || (eventInfo.IsOutBound ? eventInfo.callFromNumber : eventInfo.callToNumber),
        fromName: fromName || (eventInfo.IsOutBound ? eventInfo.callToNumber : eventInfo.callFromNumber),
      },
    });

    const smallDialogSubscription = this.isExtraSmall.subscribe((size) => {
      if (size.matches) {
        dialogRef.updateSize('100vw', '100dvh');
      } else {
        dialogRef.updateSize('950px', '80%');
      }
    });
    dialogRef.afterClosed().subscribe(() => {
      smallDialogSubscription.unsubscribe();
    });
  }

  callRecordingUrl = toSignal(
    toObservable(this.event).pipe(
      map((event) => event.eventInfo?.callRecordingUrl ?? null),
      distinctUntilChanged(),
      switchMap((url) => resolveCallRecordingUrl(this.http, url)),
    ),
    { initialValue: null },
  );

  onClick(event: MouseEvent) {
    const selectedText = window.getSelection()?.toString();

    if (selectedText) {
      return;
    }

    const target = event.target;
    if (target instanceof HTMLElement) {
      if (target.classList.contains('call-recording') || target.classList.contains('event-link')) {
        return;
      }
      this.showEvaluationButtons = !this.showEvaluationButtons;
    }
  }

  navigateToUrl(url: string): void {
    this.router.navigateByUrl(url);
  }

  hasTranscriptContent(): boolean {
    const eventInfo = this.event().eventInfo;
    if (!eventInfo?.transcriptJSON) {
      return false;
    }

    try {
      const messages = JSON.parse(eventInfo.transcriptJSON);
      return Array.isArray(messages) && messages.length > 0;
    } catch {
      return false;
    }
  }

  /** Called from the constructor, which keeps afterRenderEffect in an injection context. */
  private observeEmailPreviewOverflow(): void {
    // Observed rather than measured once per snippet change: the box also grows when a late
    // stylesheet or font lands, and re-wraps when the pane is resized, and the fade has to follow
    // both. An effect's tracking list cannot see either.
    afterRenderEffect((onCleanup) => {
      const body = this.emailPreviewBody()?.nativeElement;
      if (!body) {
        this.emailPreviewClipped.set(false);
        return;
      }

      const measure = (): void => this.emailPreviewClipped.set(body.scrollHeight > body.clientHeight + 1);
      measure();

      // Guarded as personal-email-reply-bar is: jsdom has no ResizeObserver.
      if (!window.ResizeObserver) {
        return;
      }
      const observer = new ResizeObserver(measure);
      observer.observe(body);
      onCleanup(() => observer.disconnect());
    });
  }
}
